import { z } from 'zod'
import { COMPETITION_ROLE_VALUES } from '@napaicp/shared'
import { displayNameSchema, emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'
import { USER_STATUS_VALUES } from './user'

/**
 * Platform Administration read-model DTOs and query schemas (Wave K,
 * PR-014/015, API-contract §7, PII minimization per SEC-007/PRIV-001).
 *
 * These are deliberately separate types from the competition-scoped `User`
 * and `TeamDto` schemas elsewhere in this package: a platform DTO must never
 * grow secret or competition-private fields just because a future edit to a
 * shared type adds one.
 */

const platformSortFieldSchema = z.enum(['displayName', 'email', 'createdAt', 'lastActivityAt'])

/** Server-side pagination/search/filter/sort contract for `GET /platform/users` (API-030, PR-107). */
export const platformUserListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  /** Matched against display name and email only — no free-text PII scan. */
  search: z.string().trim().max(120).optional(),
  status: z.enum(USER_STATUS_VALUES).optional(),
  /** Restrict to users holding this competition role in at least one competition. */
  role: z.enum(COMPETITION_ROLE_VALUES).optional(),
  /** Restrict to users with a competition-role relationship in this competition. */
  competitionId: publicIdSchema.optional(),
  /** Restrict to users holding an active `judge` role in at least one competition. */
  judgeOnly: z.coerce.boolean().optional(),
  sort: platformSortFieldSchema.default('displayName'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
})

export type PlatformUserListQuery = z.infer<typeof platformUserListQuerySchema>

/** One row of `GET /platform/users` (PR-014 field list, UX-012). */
export const platformUserListItemDtoSchema = z.object({
  id: publicIdSchema,
  displayName: displayNameSchema,
  email: emailSchema,
  accountStatus: z.enum(USER_STATUS_VALUES),
  globalRoleSummary: z.string(),
  competitionCount: z.number().int().min(0),
  /** Distinct competition-role names held anywhere on the platform — a summary only; the full per-competition breakdown lives in the detail/competitions endpoint (BR-060). */
  competitionRoleSummary: z.array(z.enum(COMPETITION_ROLE_VALUES)),
  judgeRelationshipCount: z.number().int().min(0),
  createdAt: utcTimestampSchema,
  lastActivityAt: utcTimestampSchema.nullable(),
})

export type PlatformUserListItemDto = z.infer<typeof platformUserListItemDtoSchema>

/** `GET /platform/users/:userId` base profile (UX "基本資料" block). */
export const platformUserDetailDtoSchema = z.object({
  id: publicIdSchema,
  displayName: displayNameSchema,
  email: emailSchema,
  accountStatus: z.enum(USER_STATUS_VALUES),
  globalRoleSummary: z.string(),
  authMethodSummary: z.array(z.string()),
  createdAt: utcTimestampSchema,
  lastActivityAt: utcTimestampSchema.nullable(),
})

export type PlatformUserDetailDto = z.infer<typeof platformUserDetailDtoSchema>

/** Exported so Competition People / Platform Judges DTOs can reuse the exact same shape (no drift). */
export const platformJudgeSummarySchema = z.object({
  profileSet: z.boolean(),
  assignmentCount: z.number().int().min(0),
  submittedScoreCount: z.number().int().min(0),
  lockedScoreCount: z.number().int().min(0),
  conflictCount: z.number().int().min(0),
})

export const platformTeamSummarySchema = z.object({
  teamId: publicIdSchema,
  teamName: z.string(),
  memberRole: z.string(),
})

/**
 * One Competition Relationship row for `GET /platform/users/:userId/competitions`
 * (PR-015, BR-060, DM-011/012). A user with roles in three competitions
 * produces three of these — they are never merged into one "primary role".
 */
export const platformUserCompetitionRelationshipDtoSchema = z.object({
  competitionId: publicIdSchema,
  competitionName: z.string(),
  competitionSlug: z.string(),
  competitionStatus: z.string(),
  roles: z.array(z.enum(COMPETITION_ROLE_VALUES)),
  grantedAt: utcTimestampSchema,
  team: platformTeamSummarySchema.nullable(),
  judge: platformJudgeSummarySchema.nullable(),
})

export type PlatformUserCompetitionRelationshipDto = z.infer<
  typeof platformUserCompetitionRelationshipDtoSchema
>

/**
 * `GET /platform/competitions/:competitionId/people` (PR-016, BR-067,
 * AC-019, UX §3 "Competition People"). The reverse query of
 * `platformUserCompetitionRelationshipDtoSchema`: fixed to one competition,
 * one row per user, roles kept as an array (never collapsed to one role).
 */
export const platformCompetitionPeopleQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().trim().max(120).optional(),
  role: z.enum(COMPETITION_ROLE_VALUES).optional(),
  sort: z.enum(['displayName', 'email', 'grantedAt']).default('displayName'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
})

export type PlatformCompetitionPeopleQuery = z.infer<typeof platformCompetitionPeopleQuerySchema>

export const platformCompetitionPersonDtoSchema = z.object({
  id: publicIdSchema,
  displayName: displayNameSchema,
  email: emailSchema,
  accountStatus: z.enum(USER_STATUS_VALUES),
  roles: z.array(z.enum(COMPETITION_ROLE_VALUES)),
  grantedAt: utcTimestampSchema,
  team: platformTeamSummarySchema.nullable(),
  judge: platformJudgeSummarySchema.nullable(),
})

export type PlatformCompetitionPersonDto = z.infer<typeof platformCompetitionPersonDtoSchema>

/**
 * Platform Judges (PR-017, BR-061, DATA-010, API-037, AC-018, AC-027, UX §3
 * "Platform Judges"). Row identity is `userId + competitionId` — the same
 * user judging two competitions produces two independent rows here, each
 * with its own governance counts. Never merge these into one row per user.
 */
const platformJudgeSortFieldSchema = z.enum([
  'displayName',
  'competitionName',
  'assignmentCount',
  'submittedScoreCount',
  'lockedScoreCount',
  'conflictCount',
  'grantedAt',
])

export const platformJudgeListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().trim().max(120).optional(),
  competitionId: publicIdSchema.optional(),
  sort: platformJudgeSortFieldSchema.default('displayName'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
})

export type PlatformJudgeListQuery = z.infer<typeof platformJudgeListQuerySchema>

export const platformJudgeListItemDtoSchema = z.object({
  userId: publicIdSchema,
  displayName: displayNameSchema,
  email: emailSchema,
  accountStatus: z.enum(USER_STATUS_VALUES),
  competitionId: publicIdSchema,
  competitionName: z.string(),
  competitionSlug: z.string(),
  competitionStatus: z.string(),
  profileSet: z.boolean(),
  assignmentCount: z.number().int().min(0),
  submittedScoreCount: z.number().int().min(0),
  lockedScoreCount: z.number().int().min(0),
  conflictCount: z.number().int().min(0),
  /** e.g. "3/5" — submitted vs. assigned, this competition only. Not a score. */
  completionSummary: z.string(),
  grantedAt: utcTimestampSchema,
})

export type PlatformJudgeListItemDto = z.infer<typeof platformJudgeListItemDtoSchema>

/**
 * Platform Audit (PR-021, API §7 "Platform Audit", UX §3, AC-020). Spans
 * every competition, unlike the existing competition-scoped
 * `auditLogDtoSchema` — adds `competitionId`/`competitionName` so the caller
 * can tell which competition (if any) an entry belongs to. Read-only.
 */
export const platformAuditLogQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  actorId: publicIdSchema.optional(),
  targetUserId: publicIdSchema.optional(),
  competitionId: publicIdSchema.optional(),
  action: z.string().max(120).optional(),
  from: utcTimestampSchema.optional(),
  to: utcTimestampSchema.optional(),
})

export type PlatformAuditLogQuery = z.infer<typeof platformAuditLogQuerySchema>

export const platformAuditLogDtoSchema = z.object({
  id: publicIdSchema,
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  /**
   * Human-readable name for the entity, when the entity type has one (team
   * name, competition name, submission's team name, affected user's display
   * name). Null when the entity type has no name (e.g. a form or round) or
   * the row has since been deleted.
   */
  entityName: z.string().nullable(),
  competitionId: publicIdSchema.nullable(),
  competitionName: z.string().nullable(),
  actor: z
    .object({
      id: publicIdSchema,
      displayName: displayNameSchema,
      email: z.string().email(),
      /**
       * The actor's CURRENT global role, not their role at the time of the
       * action — audit rows do not snapshot role, so a promoted/demoted
       * actor's historical entries show today's role, not the one they held
       * when acting.
       */
      globalRole: z.string(),
    })
    .nullable(),
  reason: z.string().nullable(),
  createdAt: utcTimestampSchema,
})

export type PlatformAuditLogDto = z.infer<typeof platformAuditLogDtoSchema>

/**
 * Platform Dashboard (PR-020, UX §3 "平台總覽", OPS-001). Every aggregate is
 * computed live and independently — one failing sub-query must not fail the
 * whole response (`partial`/`degraded` flag it instead of a hard 500).
 */
export const platformDashboardDtoSchema = z.object({
  activeUsers: z.number().int().min(0).nullable(),
  suspendedUsers: z.number().int().min(0).nullable(),
  totalCompetitions: z.number().int().min(0).nullable(),
  activeCompetitions: z.number().int().min(0).nullable(),
  judgeRelationshipCount: z.number().int().min(0).nullable(),
  platformAdministratorCount: z.number().int().min(0).nullable(),
  /** Active teams platform-wide (Competition Health Dashboard follow-up). */
  registeredTeamCount: z.number().int().min(0).nullable(),
  /** 0-100, rounded; `null` when no judge assignment exists yet (no denominator). */
  judgingCompletionRate: z.number().int().min(0).max(100).nullable(),
  recentAudit: z.array(platformAuditLogDtoSchema).nullable(),
  /** True when at least one aggregate below failed and is `null`. */
  partial: z.boolean(),
  /** Names of the metrics that failed to compute this call, if any. */
  degraded: z.array(z.string()),
  computedAt: utcTimestampSchema,
})

export type PlatformDashboardDto = z.infer<typeof platformDashboardDtoSchema>

/**
 * Platform Administration mutations (Wave L). Request schemas for the
 * dangerous, reason-required operations in PR-018/019/022, BR-062/063/064,
 * API-031..036. Response shapes reuse `platformUserDetailDtoSchema` where the
 * mutation is a User Account Status/Global Role change, so a caller sees the
 * post-mutation state without a second request.
 *
 * A non-empty `reason` is required wherever PR-022 lists the action
 * ("Suspend User、Revoke All Sessions、Grant/Revoke Competition Role、
 * Grant/Revoke Super Admin"). Reactivate is the one lifecycle mutation *not*
 * in that list (only BR-063's Suspend, not Reactivate, requires a reason),
 * so its reason stays optional.
 */
const platformMutationReasonSchema = z.string().trim().min(1).max(1000)

export const platformSuspendUserRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformSuspendUserRequest = z.infer<typeof platformSuspendUserRequestSchema>

export const platformReactivateUserRequestSchema = z.strictObject({
  reason: z.string().trim().max(1000).optional(),
})
export type PlatformReactivateUserRequest = z.infer<typeof platformReactivateUserRequestSchema>

export const platformRevokeSessionsRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformRevokeSessionsRequest = z.infer<typeof platformRevokeSessionsRequestSchema>

// ---------------------------------------------------------------------------
// Account Erasure — Platform Admin review/execute (PRIV-003/004, API-100/101,
// v3.98). Reuses `PLATFORM_PERMISSIONS.USERS_MANAGE` — the same capability
// that already gates Suspend/Reactivate/Revoke Sessions, since erasure is
// the same category of "manage another user's account lifecycle" action,
// not a distinct new capability surface.
// ---------------------------------------------------------------------------

export const platformErasureRequestListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
})
export type PlatformErasureRequestListQuery = z.infer<typeof platformErasureRequestListQuerySchema>

export const platformErasureRequestDtoSchema = z.object({
  id: publicIdSchema,
  userId: publicIdSchema,
  userDisplayName: displayNameSchema,
  userEmail: emailSchema,
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']),
  requestedReason: z.string().nullable(),
  requestedAt: utcTimestampSchema,
  reviewedByUserId: publicIdSchema.nullable(),
  reviewedByDisplayName: displayNameSchema.nullable(),
  reviewedAt: utcTimestampSchema.nullable(),
  reviewReason: z.string().nullable(),
  completedAt: utcTimestampSchema.nullable(),
})
export type PlatformErasureRequestDto = z.infer<typeof platformErasureRequestDtoSchema>

/** `POST /platform/users/erasure-requests/:id/approve` — executes the erasure immediately on approval. */
export const platformApproveErasureRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformApproveErasureRequest = z.infer<typeof platformApproveErasureRequestSchema>

export const platformRejectErasureRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformRejectErasureRequest = z.infer<typeof platformRejectErasureRequestSchema>

export const platformGrantSuperAdminRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformGrantSuperAdminRequest = z.infer<typeof platformGrantSuperAdminRequestSchema>

export const platformRevokeSuperAdminRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformRevokeSuperAdminRequest = z.infer<typeof platformRevokeSuperAdminRequestSchema>

/** `POST /api/platform/competitions/:competitionId/roles` (API-031). */
export const platformGrantCompetitionRoleRequestSchema = z.strictObject({
  userId: publicIdSchema,
  role: z.enum(COMPETITION_ROLE_VALUES),
  reason: platformMutationReasonSchema,
})
export type PlatformGrantCompetitionRoleRequest = z.infer<
  typeof platformGrantCompetitionRoleRequestSchema
>

/** `DELETE /api/platform/competitions/:competitionId/roles/:userId/:role` body (API-032). */
export const platformRevokeCompetitionRoleRequestSchema = z.strictObject({
  reason: platformMutationReasonSchema,
})
export type PlatformRevokeCompetitionRoleRequest = z.infer<
  typeof platformRevokeCompetitionRoleRequestSchema
>

export const platformCompetitionRoleGrantDtoSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  userId: publicIdSchema,
  role: z.enum(COMPETITION_ROLE_VALUES),
  alreadyGranted: z.boolean(),
  grantedAt: utcTimestampSchema,
})
export type PlatformCompetitionRoleGrantDto = z.infer<typeof platformCompetitionRoleGrantDtoSchema>

export const platformSessionsRevokedDtoSchema = z.object({
  userId: publicIdSchema,
  revoked: z.boolean(),
})
export type PlatformSessionsRevokedDto = z.infer<typeof platformSessionsRevokedDtoSchema>

/**
 * `GET /api/platform/admins` (Gap 1 closeout, PR-019, API §7 "Platform
 * Administrators", UX §3). Read-only — every current `super_admin` account.
 *
 * `grantedAt`/`grantedBy` are deliberately derived from `audit_logs`, not a
 * new `users` column (DATA-013: a platform read model must be reconstructible
 * from authoritative data, never a second source of truth). The authoritative
 * fact is still just `users.global_role = 'super_admin'`; `grantedAt`/
 * `grantedBy` are a best-effort read-model projection of the most recent
 * `super_admin.grant`/`auth.initial_super_admin_bootstrap` audit row for that
 * user, and are `null` when no such row exists (e.g. a pre-Wave-L seed with no
 * matching audit history).
 */
export const platformAdminListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().trim().max(120).optional(),
  sort: z.enum(['displayName', 'email', 'grantedAt']).default('displayName'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
})
export type PlatformAdminListQuery = z.infer<typeof platformAdminListQuerySchema>

export const platformAdminListItemDtoSchema = z.object({
  id: publicIdSchema,
  displayName: displayNameSchema,
  email: emailSchema,
  accountStatus: z.enum(USER_STATUS_VALUES),
  /** Derived from audit history — see the schema-level note above. Null when no matching audit row exists. */
  grantedAt: utcTimestampSchema.nullable(),
  grantedBy: z
    .object({
      id: publicIdSchema,
      displayName: displayNameSchema,
    })
    .nullable(),
  /** Most recent audit entry targeting this user, of any action — not limited to the grant itself. */
  recentAudit: platformAuditLogDtoSchema.nullable(),
  /**
   * Server-computed, UI-advisory only (UX §3: "撤銷最後一位 Active Super
   * Admin 的操作應在 UI 預先禁用或提示，但 server 仍必須作最終拒絕"). Never
   * the sole gate — `POST /:userId/revoke` re-validates the invariant itself
   * regardless of what this array says.
   */
  allowedActions: z.array(z.enum(['revoke'])),
})
export type PlatformAdminListItemDto = z.infer<typeof platformAdminListItemDtoSchema>
